import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Clapperboard, Mic, Music, PartyPopper, Star, Users } from "lucide-react";
import SectionHeader from "./SectionHeader";
// Placeholder photos until the final event images are ready.
import launchImg from "../../assets/images/webp/event_slider2.jpg";
import premiereImg from "../../assets/images/webp/event_slider1.jpg";
import conferenceImg from "../../assets/images/webp/event_corporate3.jpg";
import liveImg from "../../assets/images/webp/event_entertainment3.jpg";
import activationImg from "../../assets/images/webp/event_theme1.jpg";
import celebrityImg from "../../assets/images/webp/event_entertainment1.jpg";

const EVENT_TYPES = [
  { icon: PartyPopper, image: launchImg, title: "Brand & Product Launches", desc: "A reveal moment built around your product: stage, guest list, media presence and content, all planned together." },
  { icon: Clapperboard, image: premiereImg, title: "Movie Premieres & Screenings", desc: "Red carpet, guest and media management, and a screening that starts on time." },
  { icon: Mic, image: conferenceImg, title: "Conferences & Panel Sessions", desc: "Speaker line-ups, stage, sound and a run of show that keeps the day on time." },
  { icon: Music, image: liveImg, title: "Live Shows & Performances", desc: "Music nights and stage shows, with artists, technical production and audience flow handled." },
  { icon: Star, image: activationImg, title: "Activations & Experiential", desc: "Pop-ups and on-ground set-ups people can walk into, built and dismantled by our team." },
  { icon: Users, image: celebrityImg, title: "Celebrity & Creator Events", desc: "Hosts, guests and creators brought in through our influencer and celebrity network." },
];

const n = EVENT_TYPES.length;
// Shortest signed distance from the active card, wrapping around the ends.
const offsetOf = (i, active) => ((i - active + n + Math.floor(n / 2)) % n) - Math.floor(n / 2);

// Position of each slot (x in % of card width): centre, near, far, and the
// hidden off-stage spot. Tuned so cards sit ~14px apart once scaled and rotated.
const SLOTS = [
  { x: 0, scale: 1, rotate: 0 },
  { x: 99, scale: 0.86, rotate: 14 },
  { x: 190, scale: 0.72, rotate: 22 },
  { x: 260, scale: 0.6, rotate: 28 },
];

// Hover tilt: the card leans toward the cursor. Written straight to CSS
// variables so mouse moves don't re-render React.
const tilt = (e) => {
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  const x = (e.clientX - r.left) / r.width - 0.5;
  const y = (e.clientY - r.top) / r.height - 0.5;
  el.style.setProperty("--tilt-y", `${x * 18}deg`);
  el.style.setProperty("--tilt-x", `${-y * 14}deg`);
};

const untilt = (e) => {
  e.currentTarget.style.setProperty("--tilt-y", "0deg");
  e.currentTarget.style.setProperty("--tilt-x", "0deg");
};

const EventsTypes = () => {
  const [active, setActive] = useState(0);
  const [visible, setVisible] = useState(false);
  const [paused, setPaused] = useState(false);
  const section = useRef(null);
  const prevActive = useRef(active); // still the previous value during render
  const go = (dir) => setActive((a) => (a + dir + n) % n);

  useEffect(() => {
    const node = section.current;
    if (!node || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.25 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || paused) return;
    const timer = window.setInterval(() => setActive((a) => (a + 1) % n), 3500);
    return () => window.clearInterval(timer);
  }, [visible, paused]);

  useEffect(() => {
    prevActive.current = active;
  }, [active]);

  return (
    <section ref={section} className="ee-section" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocusCapture={() => setPaused(true)} onBlurCapture={() => setPaused(false)}>
      <div className="container">
        <SectionHeader
          eyebrow="Events we run"
          title="Every kind of event."
          accent="Executed end to end."
          intro="Whatever the format, the job is the same: get every detail right so the day runs smoothly. These are the events we take on."
        />
      </div>

      <div className="ee-flow" data-aos="fade-up" data-aos-once="true">
        {EVENT_TYPES.map(({ icon: Icon, image, title, desc }, i) => {
          const offset = offsetOf(i, active);
          const abs = Math.abs(offset);
          const slot = SLOTS[abs];
          const sign = Math.sign(offset);
          // The card that loops from one end to the other fades in at its new
          // spot instead of sliding across behind the whole row.
          const wraps = Math.abs(offset - offsetOf(i, prevActive.current)) > 3;
          return (
            <article
              key={title}
              className={`ee-flow-card ${offset === 0 ? "is-active" : ""} ${wraps ? "is-wrapping" : ""}`}
              onClick={() => setActive(i)}
              style={{
                transform: `translateX(${sign * slot.x}%) scale(${slot.scale}) rotateY(${-sign * slot.rotate}deg)`,
                zIndex: 10 - abs,
                opacity: abs > 2 ? 0 : 1,
              }}
            >
              <div className="ee-flow-inner" onMouseMove={tilt} onMouseLeave={untilt}>
                <img src={image} alt={title} loading="lazy" />
                <div className="ee-flow-body">
                  <span className="ee-icon">
                    <Icon size={18} />
                  </span>
                  <h3>{title}</h3>
                  <p>{desc}</p>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <div className="ee-flow-nav">
        <button type="button" onClick={() => go(-1)} aria-label="Previous event type">
          <ArrowLeft size={18} />
        </button>
        <button type="button" onClick={() => go(1)} aria-label="Next event type">
          <ArrowRight size={18} />
        </button>
      </div>
    </section>
  );
};

export default EventsTypes;
